import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

export default function MarketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [market, setMarket] = useState(null);
  const [book, setBook] = useState({ asks: [], bids: [] });
  const [trades, setTrades] = useState([]);
  const [order, setOrder] = useState({ side: 'buy', order_type: 'limit', quantity: 10, limit_price: 2500 });

  const load = async () => {
    try {
      const [marketRes, bookRes, tradesRes] = await Promise.all([
        api.get(`/tokenization/token-classes/${id}`),
        api.get(`/exchange/orderbook?token_class_id=${id}`),
        api.get(`/exchange/trades?token_class_id=${id}`),
      ]);
      setMarket(marketRes.data);
      setBook(bookRes.data);
      setTrades(tradesRes.data.trades || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load market');
    }
  };

  useEffect(() => { load(); }, [id]);

  const placeOrder = async () => {
    try {
      await api.post('/exchange/orders', { ...order, token_class_id: id, quantity: Number(order.quantity), limit_price: Number(order.limit_price) });
      toast.success('Order placed');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Order placement failed');
    }
  };

  if (!market) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className="page-shell page-stack">
      <section className="surface-panel glow-surface" style={{ padding: '1.5rem' }}>
        <div className="section-tag">{market.symbol}</div>
        <h1 className="section-title">{market.project_name}</h1>
        <p className="section-copy">{market.credit_type} · {market.methodology_code} · {market.vintage}</p>
        <div className="hero-note" style={{ marginTop: '1rem' }}>
          <div className="info-chip">Circulating: {Number(market.circulating_supply || 0).toLocaleString('en-IN')}</div>
          <div className="info-chip">Retired: {Number(market.retired_supply || 0).toLocaleString('en-IN')}</div>
          <div className="info-chip">Chain: {market.chain_name}</div>
        </div>
      </section>

      <section className="section-grid-2">
        <div className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-tag">Order book</div>
          <div className="detail-grid" style={{ marginTop: '1rem' }}>
            <div>
              <div className="stat-label">Asks</div>
              {(book.asks || []).map((row, index) => <div key={index} className="summary-row"><span>₹{Number(row.price).toLocaleString('en-IN')}</span><span>{Number(row.quantity).toLocaleString('en-IN')}</span></div>)}
            </div>
            <div>
              <div className="stat-label">Bids</div>
              {(book.bids || []).map((row, index) => <div key={index} className="summary-row"><span>₹{Number(row.price).toLocaleString('en-IN')}</span><span>{Number(row.quantity).toLocaleString('en-IN')}</span></div>)}
            </div>
          </div>
        </div>

        <div className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-tag">Place order</div>
          {user ? (
            <div style={{ display: 'grid', gap: '0.8rem', marginTop: '1rem' }}>
              <select className="form-select" value={order.side} onChange={(e) => setOrder({ ...order, side: e.target.value })}>
                <option value="buy">Buy</option>
                <option value="sell">Sell</option>
              </select>
              <input className="form-input" type="number" value={order.quantity} onChange={(e) => setOrder({ ...order, quantity: e.target.value })} placeholder="Quantity" />
              <input className="form-input" type="number" value={order.limit_price} onChange={(e) => setOrder({ ...order, limit_price: e.target.value })} placeholder="Limit price" />
              <button className="btn btn-primary" onClick={placeOrder}>Submit order</button>
            </div>
          ) : (
            <Link to="/login" className="btn btn-secondary" style={{ marginTop: '1rem' }}>Login to trade</Link>
          )}
        </div>
      </section>

      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag">Recent trades</div>
        <div style={{ marginTop: '1rem', display: 'grid', gap: '0.7rem' }}>
          {trades.map((trade) => (
            <div key={trade.id} className="summary-row">
              <span>{new Date(trade.created_at).toLocaleString('en-IN')}</span>
              <span>{Number(trade.quantity).toLocaleString('en-IN')} @ ₹{Number(trade.price).toLocaleString('en-IN')}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
